// Painted cutout rigs: AI-painted body parts (public/assets/mech/<id>/*.png) with a
// rig.json of joint pivots replace a mech's vector part shapes. The parts are loaded
// by BootScene; loadCutouts() prepares them once (mip chain for clean downscaling and
// a white emissive mask of the cyan lights for the glow sheet) and applyCutoutStyle()
// derives the mech's bone lengths, foot and torso landmarks from the pivots so the
// skeleton, attachment points and arms all match the painted proportions.
// Missing or broken assets leave the mech on its vector parts.

import type Phaser from 'phaser';
import type { EquipId, MechId, WeaponId } from '../../core/types';
import type { MechStyle, Pt } from './styles';
import type { WeaponSpec } from './weapons';
import { MECHS } from '../../data/mechs';

/** Part names a cutout rig must provide. */
export const CUTOUT_PARTS = ['pelvis', 'torso', 'head', 'fins', 'hipplate', 'thigh', 'shin', 'foot', 'upperarm', 'forearm'] as const;
export type CutoutPartName = (typeof CUTOUT_PARTS)[number];

/** Mechs that ship a painted cutout rig. */
export const CUTOUT_MECHS: readonly MechId[] = ['volt', 'bastion', 'wraith'];

/** Weapons that ship a painted prop (public/assets/weapons/<id>.png + .json). */
export const WEAPON_CUTOUTS: readonly WeaponId[] = ['pulse_carbine', 'scatterforge', 'arc_lance', 'rail_driver', 'micro_rockets', 'gravity_launcher'];

export const cutoutKeys = {
  part: (id: MechId, part: CutoutPartName) => `mechpart_${id}_${part}`,
  rig: (id: MechId) => `mechrig_${id}`,
  path: (id: MechId, file: string) => `assets/mech/${id}/${file}`,
  weapon: (id: WeaponId) => `weaponpart_${id}`,
  weaponRig: (id: WeaponId) => `weaponrig_${id}`,
  weaponPath: (id: WeaponId, ext: 'png' | 'json') => `assets/weapons/${id}.${ext}`,
};

interface RigAttach {
  to: CutoutPartName;
  at: string | [number, number];
  offset?: [number, number];
  rot?: number;
}

interface RigPartJson {
  pivots: Record<string, [number, number]>;
  scale?: number;
  behind?: boolean;
  attach?: RigAttach;
}

interface RigJson {
  pixelScale: number;
  parts: Record<CutoutPartName, RigPartJson>;
}

interface Mip {
  /** Size factor of this level relative to the source image. */
  f: number;
  color: HTMLCanvasElement;
  emit: HTMLCanvasElement | null;
}

export interface CutoutPart {
  name: string;
  /** Rig units per source pixel (pixelScale × part scale). */
  s: number;
  pivots: Record<string, Pt>;
  behind: boolean;
  mips: Mip[];
  /** Source size. */
  w: number;
  h: number;
}

/** Where the non-bone parts sit, in their parent's rig frame (computed from rig.json). */
export interface CutoutLayout {
  /** Torso part's waist pivot in the torso frame (origin = pelvis centre). */
  waist: Pt;
  /** Pelvis part's hip pivot in the pelvis frame. */
  hip: Pt;
  fins: { at: Pt; rot: number };
  hipPlate: { at: Pt; rot: number };
  /** Bone-alignment rotations (rad) for the limb images. */
  thighRot: number;
  shinRot: number;
  upperRot: number;
  foreRot: number;
}

export interface Cutout {
  parts: Record<CutoutPartName, CutoutPart>;
  layout: CutoutLayout;
  /** Head attach offset from the torso's neck pivot (torso pixels). */
  headOffset: Pt;
}

const CUTOUTS: Partial<Record<MechId, Cutout>> = {};

export function cutoutFor(id: MechId): Cutout | null {
  return CUTOUTS[id] ?? null;
}

/** Queue every cutout asset on a scene's loader (BootScene.preload). */
export function queueCutoutAssets(scene: Phaser.Scene): void {
  for (const id of CUTOUT_MECHS) {
    scene.load.json(cutoutKeys.rig(id), cutoutKeys.path(id, 'rig.json'));
    for (const p of CUTOUT_PARTS) scene.load.image(cutoutKeys.part(id, p), cutoutKeys.path(id, `${p}.png`));
  }
  for (const id of WEAPON_CUTOUTS) {
    scene.load.json(cutoutKeys.weaponRig(id), cutoutKeys.weaponPath(id, 'json'));
    scene.load.image(cutoutKeys.weapon(id), cutoutKeys.weaponPath(id, 'png'));
  }
}

/**
 * Re-spec painted weapons (before the weapon art is built): the grip pivot becomes the
 * origin; muzzle, foregrip and bounds come from the painting's pivots.
 */
export function applyWeaponCutouts(scene: Phaser.Scene, specs: Readonly<Record<WeaponId, WeaponSpec>>): void {
  for (const id of WEAPON_CUTOUTS) {
    try {
      const rig = scene.cache.json.get(cutoutKeys.weaponRig(id)) as
        | { pixelScale: number; holdReach?: number; holdDrop?: number; pivots: Record<string, [number, number]> }
        | undefined;
      const key = cutoutKeys.weapon(id);
      if (!rig || !(rig.pixelScale > 0) || !rig.pivots?.grip || !rig.pivots.muzzle || !scene.textures.exists(key)) continue;
      const src = scene.textures.get(key).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
      const part: CutoutPart = {
        name: id,
        s: rig.pixelScale,
        pivots: Object.fromEntries(Object.entries(rig.pivots).map(([k, v]) => [k, [v[0], v[1]] as Pt])),
        behind: false,
        mips: buildMips(src, src.width, src.height, specs[id].glowColor),
        w: src.width,
        h: src.height,
      };
      const spec = specs[id] as WeaponSpec;
      const g = part.pivots.grip;
      const at = (k: string): { x: number; y: number } => {
        const p = part.pivots[k] ?? g;
        return { x: (p[0] - g[0]) * part.s, y: (p[1] - g[1]) * part.s };
      };
      spec.bounds = partBounds(part, 'grip', 0);
      spec.muzzle = at('muzzle');
      spec.foregrip = at('foregrip');
      if (rig.holdReach !== undefined && rig.holdReach > 0) spec.holdReach = rig.holdReach;
      if (rig.holdDrop !== undefined && Number.isFinite(rig.holdDrop)) spec.holdDrop = rig.holdDrop;
      spec.draw = (d) => d.image(part, 'grip');
    } catch (err) {
      console.warn(`[art-mech] painted weapon ${id} failed; using vector art`, err);
    }
  }
}

// ───────────────────────────── Preparation ─────────────────────────────

function canvasOf(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

/** Hue (degrees) of an RGB colour. */
function hueOf(r: number, g: number, b: number): number {
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const c = mx - mn;
  if (c === 0) return 0;
  let h: number;
  if (mx === r) h = ((g - b) / c) % 6;
  else if (mx === g) h = (b - r) / c + 2;
  else h = (r - g) / c + 4;
  return (h * 60 + 360) % 360;
}

/** Allowed hue distance (deg) from the glow colour; the blue rim light sits further out. */
const GLOW_HUE_WINDOW = 22;

/**
 * White emissive mask of the painted lights: bright, saturated pixels whose hue matches
 * `glow` (the mech's reactor / the weapon's glow colour). The cool blue rim light has a
 * different hue and lower value, so it stays out.
 */
function emissiveMask(src: HTMLCanvasElement, glow: number): HTMLCanvasElement | null {
  const g = src.getContext('2d', { willReadFrequently: true })!;
  const img = g.getImageData(0, 0, src.width, src.height);
  const d = img.data;
  const target = hueOf((glow >> 16) & 255, (glow >> 8) & 255, glow & 255);
  let any = false;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i];
    const gg = d[i + 1];
    const b = d[i + 2];
    const mx = Math.max(r, gg, b);
    const sat = mx > 0 ? (mx - Math.min(r, gg, b)) / mx : 0;
    let dh = Math.abs(hueOf(r, gg, b) - target);
    if (dh > 180) dh = 360 - dh;
    const lit = dh <= GLOW_HUE_WINDOW && sat > 0.4 && mx > 160;
    const k = lit ? Math.min(1, (mx - 150) / 80) * Math.min(1, (sat - 0.3) / 0.3) : 0;
    const a = Math.round(d[i + 3] * k);
    d[i] = d[i + 1] = d[i + 2] = 255;
    d[i + 3] = a;
    if (a > 8) any = true;
  }
  if (!any) return null;
  const out = canvasOf(src.width, src.height);
  out.getContext('2d')!.putImageData(img, 0, 0);
  return out;
}

/** Halve repeatedly (smooth box-ish filtering) so small renders never alias. */
function buildMips(image: CanvasImageSource, w: number, h: number, glow: number): Mip[] {
  const base = canvasOf(w, h);
  base.getContext('2d')!.drawImage(image, 0, 0);
  const mips: Mip[] = [{ f: 1, color: base, emit: emissiveMask(base, glow) }];
  let f = 1;
  let prev = base;
  let prevEmit = mips[0].emit;
  while (Math.min(prev.width, prev.height) > 24) {
    f /= 2;
    const c = canvasOf(prev.width / 2, prev.height / 2);
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.drawImage(prev, 0, 0, c.width, c.height);
    let e: HTMLCanvasElement | null = null;
    if (prevEmit) {
      e = canvasOf(c.width, c.height);
      const eg = e.getContext('2d')!;
      eg.imageSmoothingEnabled = true;
      eg.imageSmoothingQuality = 'high';
      eg.drawImage(prevEmit, 0, 0, e.width, e.height);
    }
    mips.push({ f, color: c, emit: e });
    prev = c;
    prevEmit = e;
  }
  return mips;
}

/** Pick the smallest mip that is still at least as large as the effective draw scale. */
export function pickMip(part: CutoutPart, effScale: number): Mip {
  let best = part.mips[0];
  for (const m of part.mips) if (m.f >= effScale * 1.05) best = m;
  return best;
}

const sub = (a: Pt, b: Pt): Pt => [a[0] - b[0], a[1] - b[1]];
const add = (a: Pt, b: Pt): Pt => [a[0] + b[0], a[1] + b[1]];
const mul = (a: Pt, k: number): Pt => [a[0] * k, a[1] * k];
const rotate = (p: Pt, a: number): Pt => [p[0] * Math.cos(a) - p[1] * Math.sin(a), p[0] * Math.sin(a) + p[1] * Math.cos(a)];
const len = (p: Pt): number => Math.hypot(p[0], p[1]);
/** Rotation that turns direction v onto direction target. */
const alignRot = (v: Pt, target: Pt): number => Math.atan2(target[1], target[0]) - Math.atan2(v[1], v[0]);

/** Prepare every cutout whose assets loaded. Call once before the mech sheets are built. */
export function loadCutouts(scene: Phaser.Scene): void {
  for (const id of CUTOUT_MECHS) {
    try {
      const rig = scene.cache.json.get(cutoutKeys.rig(id)) as RigJson | undefined;
      if (!rig || !(rig.pixelScale > 0) || !rig.parts) continue;
      const parts = {} as Record<CutoutPartName, CutoutPart>;
      let ok = true;
      for (const name of CUTOUT_PARTS) {
        const key = cutoutKeys.part(id, name);
        const def = rig.parts[name];
        if (!def || !scene.textures.exists(key)) {
          ok = false;
          break;
        }
        const src = scene.textures.get(key).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
        const w = src.width;
        const h = src.height;
        parts[name] = {
          name,
          s: rig.pixelScale * (def.scale ?? 1),
          pivots: Object.fromEntries(Object.entries(def.pivots).map(([k, v]) => [k, [v[0], v[1]] as Pt])),
          behind: !!def.behind,
          mips: buildMips(src, w, h, MECHS[id].reactorColor),
          w,
          h,
        };
      }
      if (!ok) {
        console.warn(`[art-mech] cutout for ${id} incomplete; using vector parts`);
        continue;
      }
      const ho = rig.parts.head.attach?.offset ?? [0, 0];
      CUTOUTS[id] = { parts, layout: layoutFor(rig, parts), headOffset: [ho[0], ho[1]] };
    } catch (err) {
      console.warn(`[art-mech] cutout for ${id} failed; using vector parts`, err);
    }
  }
}

/** Placement of the non-bone parts and the limb alignment rotations. */
function layoutFor(rig: RigJson, parts: Record<CutoutPartName, CutoutPart>): CutoutLayout {
  const P = (n: CutoutPartName, k: string): Pt => parts[n].pivots[k];
  // Pelvis: its hip socket sits at the hip joints' height on the pelvis centre line.
  const hip: Pt = [0, 0];
  // Parent pixels map with the parent part's own scale (rig.json per-part scale is honoured).
  const waist = add(hip, mul(sub(P('pelvis', 'waist'), P('pelvis', 'hip')), parts.pelvis.s));
  const attachAt = (part: CutoutPartName): { at: Pt; rot: number } => {
    const a = rig.parts[part].attach;
    if (!a) return { at: [0, 0], rot: 0 };
    const parentPx: Pt = typeof a.at === 'string' ? P(a.to, a.at) : [a.at[0], a.at[1]];
    const px = add(parentPx, a.offset ? [a.offset[0], a.offset[1]] : [0, 0]);
    // Parent frame origin: torso → its waist pivot sits at `waist`; pelvis → hip pivot at `hip`.
    const [origin, originPx] = a.to === 'torso' ? [waist, P('torso', 'waist')] : [hip, P('pelvis', 'hip')];
    return { at: add(origin, mul(sub(px, originPx), parts[a.to].s)), rot: ((a.rot ?? 0) * Math.PI) / 180 };
  };
  return {
    waist,
    hip,
    fins: attachAt('fins'),
    hipPlate: attachAt('hipplate'),
    thighRot: alignRot(sub(P('thigh', 'knee'), P('thigh', 'hip')), [0, 1]),
    shinRot: alignRot(sub(P('shin', 'ankle'), P('shin', 'knee')), [0, 1]),
    upperRot: alignRot(sub(P('upperarm', 'elbow'), P('upperarm', 'shoulder')), [1, 0]),
    foreRot: alignRot(sub(P('forearm', 'fist'), P('forearm', 'elbow')), [1, 0]),
  };
}

/**
 * Re-proportion a mech style to its painted parts (bone lengths, foot, stance height,
 * torso landmarks, head centre, arm lengths). Gait and timing are kept.
 */
export function applyCutoutStyle(style: MechStyle, c: Cutout): void {
  const p = c.parts;
  const L = c.layout;
  const P = (n: CutoutPartName, k: string): Pt => p[n].pivots[k];
  const oldReach = style.thigh + style.shin + style.ankleH;
  const standRatio = style.standHip / oldReach;

  style.thigh = len(sub(P('thigh', 'knee'), P('thigh', 'hip'))) * p.thigh.s;
  style.shin = len(sub(P('shin', 'ankle'), P('shin', 'knee'))) * p.shin.s;
  style.ankleH = (P('foot', 'toe')[1] - P('foot', 'ankle')[1]) * p.foot.s;
  style.toe = (P('foot', 'toe')[0] - P('foot', 'ankle')[0]) * p.foot.s;
  style.heel = (P('foot', 'ankle')[0] - P('foot', 'heel')[0]) * p.foot.s;
  style.hipSpread = 1;
  style.hipJointY = 0;
  style.standHip = Math.round((style.thigh + style.shin + style.ankleH) * standRatio * 10) / 10;

  // Torso landmarks: torso image pixels → torso frame (waist pivot at layout.waist).
  const T = (px: Pt): Pt => add(L.waist, mul(sub(px, P('torso', 'waist')), p.torso.s));
  // The head sits on the torso's neck pivot moved by its attach offset (sunk into the collar).
  const neckPx = add(P('torso', 'neck'), c.headOffset);
  const shoulder = T(P('torso', 'shoulder'));
  style.torso = {
    neck: T(neckPx),
    shoulderF: shoulder,
    shoulderR: [shoulder[0] - 2, shoulder[1] - 1.2],
    // Chest mount ~6 px behind the reactor face.
    chest: add(T(P('torso', 'reactor')), [-6, 0]),
    back: T(P('torso', 'back')),
  };
  style.headCenter = mul(sub(P('head', 'center'), P('head', 'neck')), p.head.s);
  style.arm = {
    ...style.arm,
    upperLen: len(sub(P('upperarm', 'elbow'), P('upperarm', 'shoulder'))) * p.upperarm.s,
    foreLen: len(sub(P('forearm', 'fist'), P('forearm', 'elbow'))) * p.forearm.s,
  };
}

/** Local bounds (rig units) of a part drawn with `pivot` at the origin, rotated by rot. */
export function partBounds(part: CutoutPart, pivot: string, rot: number): { x0: number; y0: number; x1: number; y1: number } {
  const pv = part.pivots[pivot];
  const box: Pt[] = [
    [0, 0],
    [part.w, 0],
    [0, part.h],
    [part.w, part.h],
  ];
  const corners = box.map((q) => rotate(mul(sub(q, pv), part.s), rot));
  return {
    x0: Math.min(...corners.map((q) => q[0])) - 1,
    y0: Math.min(...corners.map((q) => q[1])) - 1,
    x1: Math.max(...corners.map((q) => q[0])) + 1,
    y1: Math.max(...corners.map((q) => q[1])) + 1,
  };
}

// ───────────────────────────── Wearable equipment ─────────────────────────────
// Painted equipment REPLACES one rig part (public/assets/mech/<id>/equip/*.png + equip.json):
// chest → torso, shoulders → upper arm, back → back piece (fins), legs → shin. Each part is
// scaled so its joint distance matches the part it replaces and rotated so its bone lines up,
// then drawn through the same painters while an override is active (bakeWithEquipment).

/** Rig part each equipment item replaces. */
export const EQUIP_PART: Readonly<Record<EquipId, CutoutPartName>> = {
  reactive_plating: 'torso',
  ablative_shell: 'torso',
  capacitor_core: 'torso',
  missile_pods: 'upperarm',
  storm_capacitor: 'upperarm',
  aegis_pauldrons: 'upperarm',
  overdrive_reactor: 'fins',
  tempest_reactor: 'fins',
  heat_sink_array: 'fins',
  jump_jets: 'fins',
  vector_boosters: 'shin',
  cinder_treads: 'shin',
  servo_striders: 'shin',
  anchor_greaves: 'shin',
};

/** A replacement part: the painting plus the rotation that aligns its bone with the original. */
export interface EquipPart {
  part: CutoutPartName;
  img: CutoutPart;
  /** Draw rotation (rad), used instead of the original part's rotation. */
  rot: number;
}

/** Joint pair whose distance / direction a replacement must match, per part. */
const BONE: Partial<Record<CutoutPartName, [string, string]>> = {
  torso: ['waist', 'neck'],
  upperarm: ['shoulder', 'elbow'],
  shin: ['knee', 'ankle'],
};

const EQUIP_PARTS: Partial<Record<MechId, Partial<Record<EquipId, EquipPart>>>> = {};

export const equipKeys = {
  json: (id: MechId) => `mechequip_${id}`,
  img: (id: MechId, e: EquipId) => `mechequip_${id}_${e}`,
};

/** Queue the equipment paintings (BootScene.preload, after queueCutoutAssets). */
export function queueEquipAssets(scene: Phaser.Scene): void {
  for (const id of CUTOUT_MECHS) {
    scene.load.json(equipKeys.json(id), `assets/mech/${id}/equip/equip.json`);
    for (const e of Object.keys(EQUIP_PART) as EquipId[]) scene.load.image(equipKeys.img(id, e), `assets/mech/${id}/equip/${e}.png`);
  }
}

/** Prepare every loaded equipment painting (after loadCutouts). */
export function loadEquipParts(scene: Phaser.Scene): void {
  for (const id of CUTOUT_MECHS) {
    const c = CUTOUTS[id];
    const json = scene.cache.json.get(equipKeys.json(id)) as { items?: Record<string, { part: CutoutPartName; pivots: Record<string, [number, number]> }> } | undefined;
    if (!c || !json?.items) continue;
    const out: Partial<Record<EquipId, EquipPart>> = {};
    // Back pieces have no bone: they take the mech's average joint-length ratio.
    const ratios: number[] = [];
    const pending: [EquipId, CutoutPartName, CutoutPart][] = [];
    for (const [e, def] of Object.entries(json.items) as [EquipId, { part: CutoutPartName; pivots: Record<string, [number, number]> }][]) {
      try {
        const key = equipKeys.img(id, e);
        if (!scene.textures.exists(key) || EQUIP_PART[e] !== def.part) continue;
        const src = scene.textures.get(key).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
        const base = c.parts[def.part];
        const pivots = Object.fromEntries(Object.entries(def.pivots).map(([k, v]) => [k, [v[0], v[1]] as Pt]));
        const img: CutoutPart = { name: `${id}:${e}`, s: base.s, pivots, behind: false, mips: buildMips(src, src.width, src.height, MECHS[id].reactorColor), w: src.width, h: src.height };
        const bone = BONE[def.part];
        if (bone && pivots[bone[0]] && pivots[bone[1]] && base.pivots[bone[0]] && base.pivots[bone[1]]) {
          const vb = sub(base.pivots[bone[1]], base.pivots[bone[0]]);
          const ve = sub(pivots[bone[1]], pivots[bone[0]]);
          const k = len(vb) / Math.max(1, len(ve));
          img.s = base.s * k;
          ratios.push(k);
          out[e] = { part: def.part, img, rot: alignRot(ve, vb) + baseRot(c, def.part) };
        } else pending.push([e, def.part, img]);
      } catch (err) {
        console.warn(`[art-mech] equipment ${id}/${e} failed`, err);
      }
    }
    const avg = ratios.length ? ratios.reduce((a, b) => a + b, 0) / ratios.length : 1;
    for (const [e, part, img] of pending) {
      img.s = c.parts[part].s * avg;
      out[e] = { part, img, rot: 0 };
    }
    EQUIP_PARTS[id] = out;
  }
}

/** Rotation the original part is drawn with (bone alignment), so replacements match it. */
function baseRot(c: Cutout, part: CutoutPartName): number {
  if (part === 'shin') return c.layout.shinRot;
  if (part === 'upperarm') return c.layout.upperRot;
  return 0;
}

/** Equipment this mech has a painted replacement part for. */
export function hasEquipPart(id: MechId, e: string): boolean {
  return !!EQUIP_PARTS[id]?.[e as EquipId];
}

let override: { mech: MechId; parts: Partial<Record<CutoutPartName, EquipPart>> } | null = null;

/** The active replacement for `part` of `mech` (only while bakeWithEquipment runs). */
export function equipOverride(mech: MechId, part: CutoutPartName): EquipPart | null {
  return override && override.mech === mech ? (override.parts[part] ?? null) : null;
}

/** Run `fn` with `equip`'s painted parts swapped into `mech`'s rig (synchronous). */
export function withEquipment<T>(mech: MechId, equip: readonly string[], fn: () => T): T {
  const parts: Partial<Record<CutoutPartName, EquipPart>> = {};
  for (const e of equip) {
    const p = EQUIP_PARTS[mech]?.[e as EquipId];
    if (p) parts[p.part] = p;
  }
  const prev = override;
  override = { mech, parts };
  try {
    return fn();
  } finally {
    override = prev;
  }
}
